import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "sonner";
import AuthPage from "@/pages/AuthPage";
import Dashboard from "@/pages/Dashboard";
import BeneficiaryPortal from "@/pages/BeneficiaryPortal";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Security from "@/pages/Security";
import Handbook from "@/pages/Handbook";
import ConnectReturn from "@/pages/ConnectReturn";
import DirectorsOffice from "@/pages/DirectorsOffice";
import BoardroomVault from "@/pages/BoardroomVault";
import ArbitrageBoardroom from "@/pages/ArbitrageBoardroom";
import BillingCommand from "@/pages/BillingCommand";
import PaymentResult from "@/pages/PaymentResult";
import EvidenceVault from "@/pages/EvidenceVault";
import CommandDatabase from "@/pages/CommandDatabase";
import JarvisBridge from "@/pages/JarvisBridge";
import { CookieConsent } from "@/components/uc/CookieConsent";
import { useEffect } from "react";
import { toast } from "sonner";
import "@/App.css";

function UpgradePromptListener() {
  useEffect(() => {
    const h = (e) => {
      const d = e.detail || {};
      toast.error(d.message || "Mandate cap reached — upgrade your tier.", {
        duration: 9000,
        action: {
          label: d.suggested_tier ? `Upgrade → ${d.suggested_tier.toUpperCase()}` : "View Tiers",
          onClick: () => { window.location.href = "/billing"; },
        },
      });
    };
    window.addEventListener("uc:upgrade-required", h);
    return () => window.removeEventListener("uc:upgrade-required", h);
  }, []);
  return null;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-white/50 font-mono-uc text-xs uppercase tracking-widest">
        Initializing Command Center…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function GuestOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster
          theme="dark"
          position="top-right"
          toastOptions={{
            style: {
              background: "#121212",
              border: "1px solid rgba(255,255,255,0.14)",
              borderRadius: 2,
              color: "#fff",
              fontFamily: "IBM Plex Sans",
            }
          }}
        />
        <UpgradePromptListener />
        <Routes>
          <Route path="/login" element={<GuestOnly><AuthPage mode="login" /></GuestOnly>} />
          <Route path="/register" element={<GuestOnly><AuthPage mode="register" /></GuestOnly>} />
          <Route path="/beneficiary" element={<BeneficiaryPortal />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/security" element={<Security />} />
          <Route path="/handbook" element={<Handbook />} />
          <Route path="/connect/return" element={<Protected><ConnectReturn mode="return" /></Protected>} />
          <Route path="/connect/refresh" element={<Protected><ConnectReturn mode="refresh" /></Protected>} />
          <Route path="/" element={<Protected><Dashboard /></Protected>} />
          <Route path="/office" element={<Protected><DirectorsOffice /></Protected>} />
          <Route path="/vault" element={<Protected><BoardroomVault /></Protected>} />
          <Route path="/arbitrage" element={<Protected><ArbitrageBoardroom /></Protected>} />
          <Route path="/billing" element={<Protected><BillingCommand /></Protected>} />
          <Route path="/payment/success" element={<Protected><PaymentResult mode="success" /></Protected>} />
          <Route path="/payment/cancel" element={<Protected><PaymentResult mode="cancel" /></Protected>} />
          <Route path="/evidence" element={<Protected><EvidenceVault /></Protected>} />
          <Route path="/root/database" element={<Protected><CommandDatabase /></Protected>} />
          <Route path="/jarvis" element={<Protected><JarvisBridge /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <CookieConsent />
      </AuthProvider>
    </BrowserRouter>
  );
}
